import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchAllUsers } from '../store/slices/authSlice'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import api from '../api'
import Swal from 'sweetalert2'

const ConcessionList = ({ onConcessionUpdate }) => {
  const dispatch = useDispatch()
  const { user, users } = useSelector(state => state.auth)
  const { activeSociety } = useSidebarContext()
  const [concessions, setConcessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({
    status: '',
    customer_id: '',
    plot_id: '',
    current_society_only: true // New filter to only show current society concessions
  })
  const [customers, setCustomers] = useState([])
  const [plots, setPlots] = useState([])
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  })
  const handleViewConcession = concession => {
    Swal.fire({
      title: 'Concession Details',
      html: `
      <div class="text-left">
        <p><strong>Customer:</strong> ${
          concession.customer_id?.name || 'N/A'
        }</p>
        <p><strong>Plot:</strong> ${
          concession.plot_id?.plot_number || 'N/A'
        }</p>
        <p><strong>Type:</strong> ${getConcessionTypeLabel(
          concession.concession_type
        )}</p>
        <p><strong>Amount:</strong> ${formatCurrency(
          concession.concession_amount
        )}</p>
        <p><strong>Discounted Amount:</strong> ${formatCurrency(
          concession.new_total_amount
        )}</p>
        <p><strong>Applied Date:</strong> ${formatDate(
          concession.applied_date
        )}</p>
        ${
          concession.reason
            ? `<p><strong>Reason:</strong> ${concession.reason}</p>`
            : ''
        }
      </div>
    `,
      showCloseButton: true,
      showConfirmButton: false,
      width: '600px'
    })
  }

  useEffect(() => {
    fetchConcessions()
    fetchCustomers()
    fetchPlots()

    // Test backend connectivity
    testBackendConnection()
  }, [filters, pagination.current_page])

  // Test backend connectivity
  const testBackendConnection = async () => {
    try {
      console.log('Testing backend connectivity...')
      const response = await api.get('/')
      console.log('Backend connectivity test response:', response.data)
    } catch (error) {
      console.error('Backend connectivity test failed:', error)
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      })
    }
  }

  // Filter customers when users data changes
  useEffect(() => {
    if (users && users.length > 0 && activeSociety) {
      // Filter only active customers who belong to the active society
      const customerUsers = users.filter(
        user =>
          (user.role === 'Customer' || user.role === 'customer') &&
          user.status === 'active' &&
          user.societies &&
          user.societies.some(society => society._id === activeSociety._id)
      )
      setCustomers(customerUsers)
      console.log(
        `Filtered ${customerUsers.length} customers for society: ${activeSociety.name}`
      )
    } else {
      setCustomers([])
    }
  }, [users, activeSociety])

  const fetchConcessions = async () => {
    try {
      setLoading(true)
      setError(null)

      // Check if active society is available
      if (!activeSociety) {
        setError('No active society selected. Please select a society first.')
        setLoading(false)
        return
      }

      const params = new URLSearchParams({
        page: pagination.current_page,
        limit: pagination.items_per_page,
        ...filters,
        society_id: activeSociety._id, // Add society filter
        current_society_only: filters.current_society_only.toString() // Add current society only filter
      })

      console.log('Fetching concessions with params:', params.toString())
      console.log('API base URL:', api.defaults.baseURL)
      console.log(
        'Full URL being called:',
        `${api.defaults.baseURL}/concessions?${params}`
      )
      console.log('Active Society:', activeSociety.name, activeSociety._id)
      console.log('Current Society Only Filter:', filters.current_society_only)

      const response = await api.get(`/concessions?${params}`)
      console.log('Concessions API response:', response.data)

      if (response.data.success) {
        setConcessions(response.data.data)
        setPagination(response.data.pagination)
      } else {
        console.error('API returned success: false:', response.data)
        setError(response.data.message || 'Failed to fetch concessions')
      }
    } catch (error) {
      console.error('Error fetching concessions:', error)
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers,
        data: error.response?.data
      })

      setError(
        error.response?.data?.message ||
          error.message ||
          'Failed to fetch concessions'
      )

      // Show user-friendly error message
      if (error.response?.status === 404) {
        setError(
          'No concessions found. This might be because no concessions have been applied yet.'
        )
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        setError(
          'Access denied. You may not have permission to view concessions.'
        )
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later.')
      } else if (error.code === 'ERR_NETWORK') {
        setError('Network error. Please check your connection and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomers = async () => {
    try {
      // Use Redux to fetch all users (including customers)
      await dispatch(fetchAllUsers())
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  const fetchPlots = async () => {
    try {
      if (!activeSociety) {
        setPlots([])
        return
      }

      const response = await api.get(`/societies/${activeSociety._id}`)
      if (response.data.society) {
        const societyPlots = response.data.society.plots || []
        setPlots(societyPlots)
        console.log(
          `Fetched ${societyPlots.length} plots for society: ${activeSociety.name}`
        )
      }
    } catch (error) {
      console.error('Error fetching plots:', error)
      setPlots([])
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, current_page: 1 }))
  }

  const handlePageChange = page => {
    setPagination(prev => ({ ...prev, current_page: page }))
  }

  const handleRefresh = () => {
    fetchConcessions()
  }

  const getStatusBadge = status => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-900/20 text-yellow-300 border-yellow-700',
        label: 'Pending'
      },
      approved: {
        color: 'bg-emerald-900/20 text-emerald-300 border-emerald-700',
        label: 'Approved'
      },
      rejected: {
        color: 'bg-red-900/20 text-red-300 border-red-700',
        label: 'Rejected'
      },
      cancelled: {
        color: 'bg-gray-700 text-gray-300 border-gray-600',
        label: 'Cancelled'
      }
    }

    const config = statusConfig[status] || statusConfig.pending
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}
      >
        {config.label}
      </span>
    )
  }

  const getConcessionTypeLabel = type => {
    const typeLabels = {
      percentage: 'Percentage',
      fixed_amount: 'Fixed Amount',
      total_amount: 'New Total'
    }
    return typeLabels[type] || type
  }

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount)
  }

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <h2 className='text-xl font-semibold text-white mb-2'>
          Concession List
        </h2>
        <p className='text-gray-300'>View and manage all concession requests</p>

        {/* Debug Info */}
        <div className='mt-4 p-3 bg-slate-800/60 border border-slate-700/50 rounded-md'>
          {/* <p className='text-sm text-teal-400 mb-2'>Debug Info:</p>
          <p className='text-xs text-teal-300/70'>
            Active Society: {activeSociety?.name || 'None'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Society ID: {activeSociety?._id || 'None'}
          </p>
          <p className='text-xs text-teal-300/70'>
            Total Users: {users?.length || 0}
          </p>
          <p className='text-xs text-teal-300/70'>
            Active Society Customers: {customers?.length || 0}
          </p>
          <p className='text-xs text-teal-300/70'>
            Concessions Found: {concessions?.length || 0}
          </p>
          <p className='text-xs text-teal-300/70'>
            Current Page: {pagination.current_page}
          </p>
          <p className='text-xs text-teal-300/70'>
            Total Items: {pagination.total_items}
          </p> */}

          {/* Plot Society Debug Info */}
          {concessions.length > 0 && (
            <div className='mt-2 pt-2 border-t border-slate-700/30'>
              <p className='text-xs text-teal-300/70 font-medium'>
                Plot Society Details:
              </p>
              {concessions.map((concession, index) => (
                <p key={index} className='text-xs text-teal-300/50'>
                  Plot {concession.plot_id?.plot_number}:{' '}
                  {concession.plot_id?.society_id?.name || 'Unknown Society'} (
                  {concession.plot_id?.society_id?._id || 'No ID'})
                </p>
              ))}
            </div>
          )}

          <button
            onClick={handleRefresh}
            className='mt-2 px-3 py-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs rounded hover:from-teal-600 hover:to-cyan-600 transition-all duration-200'
          >
            Refresh Concessions
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className='mb-6 p-4 bg-red-900/20 border border-red-700/50 rounded-md'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <svg
                className='h-5 w-5 text-red-400'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-400'>Error</h3>
              <div className='mt-2 text-sm text-red-300'>
                <p>{error}</p>
              </div>
              <div className='mt-4'>
                <button
                  onClick={handleRefresh}
                  className='bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-1 rounded text-sm hover:from-red-700 hover:to-red-800 transition-all duration-200'
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Society Mismatch Warning */}
      {concessions.length > 0 &&
        concessions.some(
          c => c.plot_id?.society_id?._id !== activeSociety?._id
        ) && (
          <div className='mb-6 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-md'>
            <div className='flex'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-yellow-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-yellow-400'>
                  Society Mismatch Warning
                </h3>
                <div className='mt-2 text-sm text-yellow-300'>
                  <p>
                    Some concessions shown belong to plots in different
                    societies. This can happen when:
                  </p>
                  <ul className='mt-1 list-disc list-inside'>
                    <li>Customers belong to multiple societies</li>
                    <li>Plots were transferred between societies</li>
                    <li>Data inconsistencies exist</li>
                  </ul>
                  <p className='mt-2'>
                    Only concessions for plots in{' '}
                    <strong>{activeSociety?.name}</strong> should be displayed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Filters */}
      <div className='bg-slate-800/60 border border-slate-700/50 rounded-lg p-4 mb-6'>
        <h3 className='text-lg font-medium text-teal-400 mb-4'>Filters</h3>
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
          {/* <div> */}
          {/* <label className='block text-sm font-medium text-teal-300/70 mb-2'>
              Status
            </label>
            <select
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              className='w-full px-3 py-2 border border-slate-700/50 bg-slate-800/60 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200'
            >
              <option value=''>All Statuses</option>
              <option value='pending'>Pending</option>
              <option value='approved'>Approved</option>
              <option value='rejected'>Rejected</option>
              <option value='cancelled'>Cancelled</option>
            </select> */}
          {/* </div> */}

          <div>
            <label className='block text-sm font-medium text-teal-300/70 mb-2'>
              Customer
            </label>
            <select
              value={filters.customer_id}
              onChange={e => handleFilterChange('customer_id', e.target.value)}
              className='w-full px-3 py-2 border border-slate-700/50 bg-slate-800/60 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200'
            >
              <option value=''>All Customers</option>
              {customers.map(customer => (
                <option key={customer._id} value={customer._id}>
                  {customer.customerData?.name ||
                    customer.name ||
                    customer.username}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-teal-300/70 mb-2'>
              Plot
            </label>
            <select
              value={filters.plot_id}
              onChange={e => handleFilterChange('plot_id', e.target.value)}
              className='w-full px-3 py-2 border border-slate-700/50 bg-slate-800/60 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200'
            >
              <option value=''>All Plots</option>
              {plots.map(plot => (
                <option key={plot._id} value={plot._id}>
                  Plot {plot.plot_number} - Block {plot.block}
                </option>
              ))}
            </select>
          </div>

          {/* <div className='flex items-center'>
            <input
              type='checkbox'
              id='current_society_only'
              checked={filters.current_society_only}
              onChange={e =>
                handleFilterChange('current_society_only', e.target.checked)
              }
              className='w-4 h-4 text-teal-500 bg-slate-800 border-slate-700/50 rounded focus:ring-teal-500 focus:ring-2'
            />
            <label
              htmlFor='current_society_only'
              className='ml-2 text-sm text-teal-300/70'
            >
              Current Society Only
            </label>
          </div> */}
        </div>
      </div>

      {/* Concessions Table */}
      <div className='bg-slate-800/60 border border-slate-700/50 rounded-lg overflow-hidden'>
        {loading ? (
          <div className='p-8 text-center'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500'></div>
            <p className='mt-2 text-teal-300'>Loading concessions...</p>
          </div>
        ) : concessions.length === 0 ? (
          <div className='p-8 text-center'>
            <div className='text-teal-300 mb-4'>
              <p className='text-lg font-medium mb-2'>No concessions found</p>
              <p className='text-sm text-teal-300/70'>
                {error
                  ? 'There was an error loading concessions.'
                  : 'No concession requests have been submitted yet.'}
              </p>
            </div>
            {!error && (
              <div className='text-sm text-teal-300/70'>
                <p>To see concessions here:</p>
                <ul className='mt-2 space-y-1'>
                  <li>
                    • Apply for a new concession using the "Apply Concession"
                    tab
                  </li>
                  <li>
                    • Check if there are any existing concessions in the
                    database
                  </li>
                  <li>• Verify your user permissions</li>
                </ul>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Summary Info */}
            <div className='mb-4 p-3 bg-slate-900/60 border border-slate-700/50 rounded-md'>
              <div className='flex items-center justify-between'>
                <div className='text-sm text-teal-300'>
                  <span className='font-medium'>
                    Showing {concessions.length} concession
                    {concessions.length !== 1 ? 's' : ''}
                  </span>
                  {filters.current_society_only && (
                    <span className='ml-2 text-teal-300/70'>
                      (filtered to {activeSociety?.name} only)
                    </span>
                  )}
                  {!filters.current_society_only && (
                    <span className='ml-2 text-teal-300/70'>
                      (including all societies)
                    </span>
                  )}
                </div>
                <div className='text-xs text-teal-300/70'>
                  {
                    concessions.filter(
                      c => c.plot_id?.society_id?._id === activeSociety?._id
                    ).length
                  }{' '}
                  from current society
                  {concessions.some(
                    c => c.plot_id?.society_id?._id !== activeSociety?._id
                  ) && (
                    <span className='ml-2 text-yellow-400'>
                      •{' '}
                      {
                        concessions.filter(
                          c => c.plot_id?.society_id?._id !== activeSociety?._id
                        ).length
                      }{' '}
                      from other societies
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-slate-700/50'>
                <thead className='bg-slate-900/60'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Customer
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Booking
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Plot
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Type
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Amount
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Status
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Applied Date
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-slate-800/60 divide-y divide-slate-700/50'>
                  {concessions.map(concession => (
                    <tr
                      key={concession._id}
                      className='hover:bg-slate-700/50 transition-colors duration-150'
                    >
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm text-white'>
                          {concession.customer_id?.name ||
                            concession.customer_id?.username ||
                            'N/A'}
                        </div>
                        <div className='text-sm text-teal-300/70'>
                          {concession.customer_id?.contactInfo?.phone ||
                            concession.customer_id?.phone ||
                            'N/A'}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm text-white'>
                          Booking #
                          {concession.booking_id?.booking_number || 'N/A'}
                        </div>
                        <div className='text-sm text-teal-300/70'>
                          {concession.plot_id?.plot_number || 'N/A'}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm text-white'>
                          {concession.plot_id?.plot_number || 'N/A'}
                        </div>
                        <div className='text-sm text-teal-300/70'>
                          Block: {concession.plot_id?.block || 'N/A'}
                        </div>
                        <div className='text-xs text-teal-300/50'>
                          Society:{' '}
                          {concession.plot_id?.society_id?.name || 'N/A'}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm text-white'>
                          {getConcessionTypeLabel(concession.concession_type)}
                        </div>
                        <div className='text-sm text-teal-300/70'>
                          {concession.concession_type === 'percentage'
                            ? `${concession.concession_value}%`
                            : formatCurrency(concession.concession_value)}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm text-white'>
                          {formatCurrency(concession.concession_amount)}
                        </div>
                        <div className='text-sm text-teal-300/70'>
                          {formatCurrency(concession.new_total_amount)}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        {getStatusBadge(concession.status)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-teal-300/70'>
                        {formatDate(concession.applied_date)}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                        <button
                          onClick={() => handleViewConcession(concession)}
                          className='text-teal-400 hover:text-teal-300 transition-colors duration-150'
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className='bg-slate-900/60 px-4 py-3 border-t border-slate-700/50 sm:px-6'>
                <div className='flex items-center justify-between'>
                  <div className='flex-1 flex justify-between sm:hidden'>
                    <button
                      onClick={() =>
                        handlePageChange(pagination.current_page - 1)
                      }
                      disabled={pagination.current_page === 1}
                      className='relative inline-flex items-center px-4 py-2 border border-slate-700/50 text-sm font-medium rounded-md text-teal-300 bg-slate-800/60 hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        handlePageChange(pagination.current_page + 1)
                      }
                      disabled={
                        pagination.current_page === pagination.total_pages
                      }
                      className='ml-3 relative inline-flex items-center px-4 py-2 border border-slate-700/50 text-sm font-medium rounded-md text-teal-300 bg-slate-800/60 hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
                    >
                      Next
                    </button>
                  </div>
                  <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
                    <div>
                      <p className='text-sm text-teal-300'>
                        Showing{' '}
                        <span className='font-medium'>
                          {(pagination.current_page - 1) *
                            pagination.items_per_page +
                            1}
                        </span>{' '}
                        to{' '}
                        <span className='font-medium'>
                          {Math.min(
                            pagination.current_page * pagination.items_per_page,
                            pagination.total_items
                          )}
                        </span>{' '}
                        of{' '}
                        <span className='font-medium'>
                          {pagination.total_items}
                        </span>{' '}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'>
                        <button
                          onClick={() =>
                            handlePageChange(pagination.current_page - 1)
                          }
                          disabled={pagination.current_page === 1}
                          className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-700/50 bg-slate-800/60 text-sm font-medium text-teal-300 hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
                        >
                          Previous
                        </button>

                        {Array.from(
                          { length: pagination.total_pages },
                          (_, i) => i + 1
                        ).map(page => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 ${
                              page === pagination.current_page
                                ? 'z-10 bg-gradient-to-r from-teal-500 to-cyan-500 border-teal-500 text-white'
                                : 'bg-slate-800/60 border-slate-700/50 text-teal-300 hover:bg-slate-700/50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() =>
                            handlePageChange(pagination.current_page + 1)
                          }
                          disabled={
                            pagination.current_page === pagination.total_pages
                          }
                          className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-700/50 bg-slate-800/60 text-sm font-medium text-teal-300 hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ConcessionList
